import { Building2, ShieldAlert } from 'lucide-react';
import { useAuthStore, SUPERADMIN_TENANT_ID } from '../stores/authStore';

// Fase T.4 (2026-05-17) — tenant-aware topbar chip.
//
// Source of truth: the JWT `tenant_id` claim, surfaced via /auth/me
// and persisted in authStore. The chip is intentionally NOT a
// switcher: the JWT binds each identity to a single tenant.
//
// Visibility rules (2026-05-24):
//   - `_superadmin` → amber chip "audit · _superadmin" (always shown).
//     Critical visual cue that cross-tenant reads are possible.
//   - Non-default tenant (e.g. `ignis_pilot`, `demo_showcase`) →
//     primary chip. Useful so the user knows they are NOT on TMEIC.
//   - Default tenant (`tmeic_rambler`) on a non-admin role → hidden.
//     Redundant noise: the JWT can never reach another tenant.
const DEFAULT_TENANT_ID = 'tmeic_rambler';

export function TenantBadge() {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;

  const tenantId = user.tenant_id || 'unknown';
  const isSuperadmin = tenantId === SUPERADMIN_TENANT_ID;

  if (isSuperadmin) {
    return (
      <div
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs font-medium"
        title="BEAI audit identity — can read across tenants. All requests logged."
      >
        <ShieldAlert className="w-3.5 h-3.5" />
        <span>audit · {tenantId}</span>
      </div>
    );
  }

  // Hide for default-tenant non-admin users (the common case): the chip
  // would just repeat what the rest of the UI already implies.
  if (tenantId === DEFAULT_TENANT_ID) {
    return null;
  }

  return (
    <div
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-dark-800 border border-dark-700 text-dark-200 text-xs"
      title={`Tenant: ${tenantId} · role: ${user.tenant_role || user.role}`}
    >
      <Building2 className="w-3.5 h-3.5 text-primary-400" />
      <span className="font-medium">{tenantId}</span>
    </div>
  );
}
